import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useOutletContext } from "react-router-dom";
import ThemeConstants from "../../../config/theme/theme_constants";
import HomeIcon from "./home_icon";
import RewardsIcon from "./rewards_icon";
import SurveyIcon from "./survey_icon";
import CareerIcon from "./career_icon";
import useWindowSize from "../../utils/use_windows_size";
import { useTranslation } from "../../../i18n/useTranslation";
import { UserInterface } from "../user/domain/interfaces/user_interface";

interface NavbarElement {
  id: string;
  name: string;
  url: string;
  icon: React.ReactElement;
}

interface NavbarProps {
  className?: string;
}

const WRAPPER_PX = 24;
const UL_PX_MOBILE = 8;
const ICON_OFFSET = 4.5;
const PAD_TARGET = 20;

const Navbar: React.FC<NavbarProps> = ({ className }) => {
  const { t } = useTranslation();
  const theme = ThemeConstants();
  const location = useLocation().pathname;
  const { width } = useWindowSize();
  const isDesktop = width >= 768;
  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);
  const { company } = useOutletContext<UserInterface>();
  const [layout, setLayout] = useState<{ padLeft: number; padRight: number; minWidth?: number } | null>(null);

  const navElements: NavbarElement[] = useMemo(() => {
    const baseElements = [
      { id: 'home', name: t('navbar.home'), url: '/', icon: <HomeIcon selected={location === '/'}/>},
      { id: 'rewards', name: t('navbar.rewards'), url: '/rewards', icon: <RewardsIcon selected={location === '/rewards'}/>},
      { id: 'survey', name: t('navbar.survey'), url: '/survey', icon: <SurveyIcon selected={location === '/survey'}/>},
    ];
    if (company?.permissions?.ressource === true || company?.permissions?.course === true) {
      baseElements.push({ id: 'career', name: t('navbar.explore'), url: '/career', icon: <CareerIcon selected={location.startsWith('/career')}/> });
    }
    return baseElements;
  }, [location, company?.permissions?.course, company?.permissions?.ressource, t]);

  // Latest item count read without adding it as a dependency (layout is only recomputed on isDesktop/width change).
  const navCountRef = useRef(navElements.length);
  useLayoutEffect(() => {
    navCountRef.current = navElements.length;
  }, [navElements.length]);

  useLayoutEffect(() => {
    const contentWidths = contentRefs.current.map(r => r?.getBoundingClientRect().width ?? 0);
    if (contentWidths.some(w => w === 0)) return;
    const maxContent = Math.max(...contentWidths);

    if (isDesktop) {
      setLayout({ padLeft: PAD_TARGET - ICON_OFFSET, padRight: PAD_TARGET });
    } else {
      const viewport = document.documentElement.clientWidth;
      const availTotal = viewport - WRAPPER_PX - UL_PX_MOBILE;
      const padEach = Math.min(PAD_TARGET, Math.max(0, (availTotal / navCountRef.current - maxContent) / 2));
      setLayout({ padLeft: padEach, padRight: padEach, minWidth: maxContent + 2 * padEach });
    }
  }, [isDesktop, width]);

  return (
    <nav className={`${className ?? ''} bg-white rounded-full max-w-full`} style={{ boxShadow: '0 4px 10px rgba(0,0,0,0.25)' }}>
      <ul className="flex items-center max-md:px-1 md:px-2 max-md:py-1.25 md:py-2.25 text-xs" style={{ color: '#494949' }}>
        {navElements.map((e, i) => {
          const selected = (e.url === '/' && location === '/') || (e.url !== '/' && location.startsWith(e.url));
          return (<li key={e.id}>
            <Link
              to={e.url}
              className="flex items-center justify-center h-full rounded-full max-md:py-1.25 md:py-2.25 whitespace-nowrap"
              style={{
                backgroundColor: selected ? theme.UvibesPink : 'transparent',
                ...(layout ? {
                  paddingLeft: `${layout.padLeft}px`,
                  paddingRight: `${layout.padRight}px`,
                  ...(layout.minWidth !== undefined ? { minWidth: `${layout.minWidth}px` } : {}),
                } : {}),
              }}>
              <div
                ref={(el) => { contentRefs.current[i] = el; }}
                className="flex max-md:flex-col items-center md:gap-2.5">
                {e.icon}
                <span className={`text-xs font-regular text-center ${selected ? 'text-white' : ''}`}>{e.name}</span>
              </div>
            </Link>
          </li>);
        })}
      </ul>
    </nav>
  );
};

export default Navbar;
